import { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBrand } from '../../brands/contexts/BrandContext';
import { budzdorovAuth, BudzdorovAuthResponse, setTokens, getCurrentUser } from '../utils/apiUtils';
import { setUser } from '../../userSlice';
import { addChat, selectChat, setMessagesForChat, updateChat, Chat } from '../../chatSlice';
import { serverChatToLocal, serverMessageToLocal, MessageSender, MessageType } from '../types/chat';
import store from '../../store';

/**
 * Хук для обработки авторизации через BudZdorov API с query параметрами userId и protocolId
 * Работает только для бренда budzdorov
 */
export const useBudzdorovAuth = () => {
    const { brandId } = useBrand();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const processedRef = useRef<string | null>(null); // Храним обработанные параметры

    useEffect(() => {
        // Проверяем, что это бренд budzdorov
        if (brandId !== 'budzdorov') {
            return;
        }

        // Парсим query параметры
        const searchParams = new URLSearchParams(location.search);
        const userIdParam = searchParams.get('userId');
        const protocolIdParam = searchParams.get('protocolId');

        // Для /admin достаточно userId; для остальных путей нужны оба параметра
        const isAdminPath = location.pathname === '/admin';
        if (!userIdParam || (!isAdminPath && !protocolIdParam)) {
            return;
        }

        const userId = parseInt(userIdParam, 10);
        const protocolId = protocolIdParam ? parseInt(protocolIdParam, 10) : 0;

        // Проверяем валидность параметров
        if (isNaN(userId) || (!isAdminPath && isNaN(protocolId))) {
            console.error('Неверные параметры userId или protocolId');
            const errorMessage = 'Неверные параметры авторизации. Проверьте корректность userId и protocolId.';
            setError(errorMessage);
            navigate(`/auth-error?error=${encodeURIComponent(errorMessage)}&userId=${userIdParam}&protocolId=${protocolIdParam ?? ''}`, { replace: true });
            return;
        }

        // Создаем уникальный ключ для этих параметров
        const paramsKey = `${userId}-${protocolId}`;

        // Проверяем, не обрабатывали ли мы уже эти параметры
        if (processedRef.current === paramsKey || isProcessing) {
            return;
        }

        // Обрабатываем авторизацию
        const handleAuth = async () => {
            setIsProcessing(true);
            setError(null);
            processedRef.current = paramsKey;

            try {
                console.log(`Авторизация BudZdorov: userId=${userId}, protocolId=${protocolId}`);

                // Вызываем API авторизации
                const authResponse: BudzdorovAuthResponse = await budzdorovAuth(userId, protocolId);

                // Сохраняем токены
                setTokens(authResponse.access_token, authResponse.refresh_token);

                // Устанавливаем время последней синхронизации сразу после сохранения токенов,
                // чтобы предотвратить автоматическую синхронизацию через useChatSync,
                // которая может конфликтовать с данными, полученными через BudZdorov API
                const syncTime = new Date().toISOString();
                localStorage.setItem('lastSyncTime', syncTime);

                // Получаем информацию о пользователе
                const userData = await getCurrentUser();
                if (!userData) {
                    throw new Error('Не удалось получить данные пользователя');
                }

                // Устанавливаем пользователя в Redux
                dispatch(setUser({
                    email: userData.email,
                    phone_number: userData.phone_number || '',
                    first_name: userData.first_name || '',
                    last_name: userData.last_name || '',
                    id: userData.id || 0,
                    role_name: userData.role_name || '',
                    role_id: userData.role_id || 0,
                    specialty: userData.specialty || '',
                    is_active: userData.is_active,
                    organization: userData.organization || 'Public',
                    ...userData
                }));

                // Конвертируем сообщения из ответа API в локальный формат
                const localMessages = authResponse.chat.messages.map(msg => {
                    // Конвертируем серверное сообщение в формат ServerMessage
                    const serverMessage = {
                        id: msg.id,
                        chat_id: msg.chat_id,
                        sender: msg.sender as MessageSender,
                        text: msg.text,
                        type: msg.type as MessageType,
                        time: msg.time,
                        created_at: msg.created_at,
                        updated_at: msg.created_at, // Используем created_at если updated_at отсутствует
                        json_content: msg.json_content,
                        title: msg.title,
                        specialty: msg.specialty
                    };
                    return serverMessageToLocal(serverMessage);
                });

                // Конвертируем чат из ответа API в локальный формат
                const localChat = serverChatToLocal(
                    {
                        id: authResponse.chat.id,
                        title: authResponse.chat.title,
                        description: authResponse.chat.description,
                        user_id: authResponse.chat.user_id,
                        is_active: authResponse.chat.is_active,
                        created_at: authResponse.chat.created_at,
                        updated_at: authResponse.chat.updated_at
                    },
                    localMessages
                );

                // Проверяем, существует ли уже чат с таким ID
                const currentState = store.getState();
                const existingChat = currentState.chat.chats.find((chat: Chat) => chat.id === localChat.id);

                if (existingChat) {
                    // Обновляем существующий чат
                    dispatch(updateChat({
                        id: localChat.id,
                        updates: {
                            title: localChat.title,
                            lastMessageTime: localChat.lastMessageTime,
                            serverId: localChat.serverId,
                            isDirty: false
                        }
                    }));
                } else {
                    // Добавляем новый чат в Redux store
                    dispatch(addChat(localChat));
                }

                // Устанавливаем сообщения для чата (история уже загружена из API)
                dispatch(setMessagesForChat({
                    chatId: localChat.id,
                    messages: localMessages
                }));

                // Выбираем этот чат
                dispatch(selectChat(localChat.id));

                // Обновляем время последней синхронизации после успешной загрузки чата
                localStorage.setItem('lastSyncTime', new Date().toISOString());

                console.log(`✅ Авторизация BudZdorov успешна. Чат ID: ${authResponse.chat_id}, сообщений: ${localMessages.length}`);

                // После авторизации возвращаемся на тот же путь (сохраняем /admin если оттуда пришли),
                // но без query-параметров авторизации, чтобы не зациклиться
                const targetPath = isAdminPath ? '/admin' : '/dialogs';
                const remainingSearch = (() => {
                    const p = new URLSearchParams(location.search);
                    p.delete('userId');
                    p.delete('protocolId');
                    const s = p.toString();
                    return s ? `?${s}` : '';
                })();
                navigate(`${targetPath}${remainingSearch}`, { replace: true });

            } catch (err: any) {
                console.error('Ошибка авторизации BudZdorov:', err);
                const errorMessage = err.message || 'Ошибка авторизации';
                setError(errorMessage);

                // В случае ошибки очищаем токены
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                processedRef.current = null; // Сбрасываем флаг при ошибке

                // Перенаправляем на страницу ошибки с сообщением об ошибке
                navigate(`/auth-error?error=${encodeURIComponent(errorMessage)}&userId=${userId}&protocolId=${protocolId}`, { replace: true });
            } finally {
                setIsProcessing(false);
            }
        };

        handleAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandId, location.search]);

    return {
        isProcessing,
        error
    };
};
